import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PlaceService } from '@app/modules/model/svcplace';
import { ScaleDevice } from '@app/modules/model/scale';

import { languageService, languageSupport } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-scale-devices',
    templateUrl: 'ui-scale.html',
    styleUrls: [
      'ui-scale.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiScaleDevices extends languageSupport implements OnInit, OnChanges, OnDestroy { 
    @Input('service') _service: PlaceService = null;
    @Input('editdevice') _editdevice: any = null;

    /*****************************/
    /* COMPONENT DATA SHARING    */
    /*****************************/

    private get EditDevice(){
        return this._editdevice;
    }

    DiscardChanges(){
        this._RefreshDevices();
        this.IsDirty = false;
        this.LoadScaleForm();
    }

    @Output('OnUpdate') _onupdate = new EventEmitter <any> ();
    private set EditDevice(value){
        this._editdevice = value;

        this._onupdate.next();
        this.change.markForCheck();
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.markForCheck();
        this._onchange.emit();
    }

    get IsValid(){
        return true;
    }

    private _isdirty = false;
    get IsDirty(){
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }
        
        return this._isdirty;
    }

    set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.ScaleForm.markAsPristine();
        }
        
        this.OnViewChanged();
    }

    private _scales: Array<ScaleDevice> = [];
    get PlaceScales(){
        return this._scales || [];
    }

    /*****************************/
    /* COMPONENT DATA ACCESS     */
    /*****************************/

    get ScaleModels(){
        let _options = [ 
            "TEST", "BARTRAX" 
        ];

        let _values = [];
        for(let _option of _options){
            _values.push({
                value: _option, text: _option
            })
        }

        return _values;
    }
    
    private _scaleForm = null;
    get ScaleForm(){
        if (this._scaleForm == null){
            this.LoadScaleForm();
        }
        return this._scaleForm;
    }

    private _getScaleModel(value){
        let _text = null;

        for(let _option of this.ScaleModels){
            if (_option.value == value){
                _text = _option.text;
            }
        }

        return { value: value, text: _text }; 
    }    
    
    private LoadScaleForm(){
        let _name = null;
        let _model = null;
        let _port = null;
    
        if (this.EditDevice){
            _name = this.EditDevice.name;
            _model = this._getScaleModel(this.EditDevice.model);
            _port = this.EditDevice.port;
        }

        if (this._scaleForm == null){
            this._scaleForm = new FormGroup({
                name: new FormControl(_name, [
                    // no validators required
                ]),
                model: new FormControl(_model, [
                    Validators.required
                ]),
                port: new FormControl(_port, [
                    // no validators required
                ])
            })
        }
        else {
            this._scaleForm.patchValue({
                name: _name, 
                model: _model,
                port: _port
            });
        }

        this._scaleForm.markAsPristine();
        setTimeout(() => {
            this._scaleForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    private _RefreshDevices(){
        this._scales = this._PlaceScales;
        this.IsDirty = false;

        this.OnViewChanged();      // update devices view on parent
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private get _PlaceScales(){
        let _scales = [];
        if (this._service){
            for(let _scale of this._service.scales)
            if (_scale.IsValid){
                _scales.push(_scale);
            }
        }

        return _scales;
    }

    ngOnInit(){
        this._RefreshDevices();
    }

    ngOnChanges(changes: SimpleChanges) : void {
        if ('_service' in changes){     // only refresh on service change
            this._RefreshDevices();

            if (this._service_subscription){
                this._service_subscription.unsubscribe();
                this._service_subscription = null;
            }
    
            this._service_subscription = this._service.OnRefresh.subscribe(
            data => {
                if (!this.IsDirty){
                    this._RefreshDevices();
                }
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }        
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    OnModelChanged(){
        let _model = this.ScaleForm.get('model').value?.value;
        if (_model == 'BARTRAX'){
            this.ScaleForm.get('port').setValidators([ Validators.required ]);
        }
        else {
            this.ScaleForm.get('port').clearValidators();
        }

        this.ScaleForm.get('port').setErrors(null);
        this.ScaleForm.get('port').updateValueAndValidity();
        
        this.change.markForCheck();
    }

    private _expand = false;
    get ExpandScales(){
        return this._expand;
    }

    @ViewChild('devices', { static: false }) _devices: ElementRef;
    OnExpandScales(){
        this._expand = !this._expand;

        if (this._devices){
            let _devices = this._devices.nativeElement;
            if (this._expand){
                _devices.style.height = _devices.scrollHeight + 'px'; 
                setTimeout(() => {
                    _devices.style.height = 'auto';
                }, 200);                
            }
            else {
                _devices.style.height = _devices.scrollHeight + 'px';
                _devices.offsetHeight;                
                _devices.style.height = '0px';     
            }    
        }

        this.change.markForCheck();
    }

    OnAddDevice(){
        this._isdirty = true;

        let _device = new ScaleDevice(null, this.data);
        if (_device){
            _device.rasppi = this._service;
            _device.status = 'AC'
        }

        this.EditDevice = _device;
        this.LoadScaleForm();
        this.EditDevice['operation'] = 'add';

        let _idx = this._scales.indexOf(_device);
        if (_idx == -1){
            this._scales.unshift(this.EditDevice);
        }

        this._expand = true;
        this.change.markForCheck();
    }

    OnSetDevice(device){
        this.EditDevice = device;
        this.LoadScaleForm();
        this.EditDevice['operation'] = 'set';

        this.change.markForCheck();
    }

    private _ScalesUpdated(){
        let _placescales = this.PlaceScales;
        if (_placescales.length == this._scales.length){
            return _placescales.every(_scale => {
                return this._scales.includes(_scale);
            })
        }
        return false;
    }

    private _DelDevice(_device){
        _device.status = 'DE';

        let _idx = this._scales.indexOf(_device);
        if (_idx != -1){
            this._scales.splice(_idx, 1);
        }

        this.EditDevice = null;
        this._isdirty = this._ScalesUpdated();
        this.change.markForCheck();
    }

    async OnDelDevice(device){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_scale_delete_confirm'),
            message: this.lang.tr('@place_scale_delete_message', [ device.name || this.lang.tr('@place_device_scale_noname') ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._DelDevice(device);
            }
        });

        await alert.present();
    }

    /****************************/
    /* APPLY CHANGES            */
    /****************************/

    private _ApplyScaleForm(){
        this.EditDevice.name = this.ScaleForm.get('name').value;
        this.EditDevice.model = this.ScaleForm.get('model').value?.value;
        this.EditDevice.port = this.ScaleForm.get('port').value;

        if (this.EditDevice.name){
            this.EditDevice.name = this.EditDevice.name.trim();
        }

        if (this.EditDevice.port){
            this.EditDevice.port = this.EditDevice.port.trim();
        }
    }

    /****************************/
    /* COMMIT ACTIONS           */
    /****************************/

    OnAcceptDevice(){
        this._ApplyScaleForm();
        this.ScaleForm.markAsPristine();

        this.EditDevice = null;
        this.IsDirty = true;
    }

    OnCancelDevice(){
        this.ScaleForm.markAsPristine();

        if (this.EditDevice['operation'] == 'add'){
            this._DelDevice(this.EditDevice);
        }

        this.EditDevice = null;
    }
}